import IconButton from 'components/IconButton';
import ModalDialog from 'components/ModalDialog';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { Fragment, Suspense, useState, useEffect, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Collapse,
  Button,
} from 'reactstrap';
import { currentChartTypeAtom } from 'views/state';

import { EPType } from './config';
import CurrentCharts from './currentCharts';
import EPList from './EPList';
import HistoryCharts from './historyCharts';
import {
  severityFiltersAtom,
  timeSpanAtom,
  intervalAtom,
  epTypeAtom,
  showHistoryChartCountsAtom,
  historyChartsOpenAtom,
  currentVulnChartsOpenAtom,
  isObsoleteGroupingVulnListingOpenAtom,
  isGroupingByQidVulnListingOpenAtom,
  hostCountsAtom,
} from './state';
import HistoryChartHeader from '../components/HistoryChart/HistoryChartHeader';
import SeverityFilterDropdown from '../components/SeverityFilterDropdown';

import '../styles.css';
import { ErrorBoundary } from 'components/Errors/ErrorBoundary';
import LoadingScreen from 'components/LoadingScreen';

import VulnerabilityMetrics from './VulnMetrics';
import {
  ChartType,
  DashboardType,
  ModalDialogData,
  TooltipLabelsType,
} from '../types';

const EPDashboard = () => {
  const [epType, setEPType] = useAtom(epTypeAtom);
  const [currentChartType, setCurrentChartType] = useAtom(currentChartTypeAtom);
  const hostCounts = useAtomValue(hostCountsAtom);

  // TOGGLE THE COLLAPSE FOR HISTORY CHARTS AND CURRENT CHARTS SECTIONS
  const [historyChartsOpen, setHistoryChartsOpen] = useAtom(
    historyChartsOpenAtom,
  );
  const [currentVulnChartsOpen, setCurrentVulnChartsOpen] = useAtom(
    currentVulnChartsOpenAtom,
  );
  const setIsObsoleteGroupingVulnListingOpen = useSetAtom(
    isObsoleteGroupingVulnListingOpenAtom,
  );
  const setIsGroupingByQidVulnListingOpen = useSetAtom(
    isGroupingByQidVulnListingOpenAtom,
  );

  const [modalComponentData, setModalComponentData] =
    useState<ModalDialogData>();

  const listingScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.location.href.includes(`#${DashboardType.EP}`)) {
      setTimeout(() => {
        if (listingScrollRef.current) {
          listingScrollRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 1000);
    }
  });

  const ExternalIcon = (
    <span className="fa-stack-lg" style={{ marginRight: '5px' }}>
      <i className="fa fa fa-desktop fa-stack-lg fa-lg"></i>
      <small
        style={{
          verticalAlign: 'top',
          marginLeft: '-16px',
          fontWeight: 'bold',
        }}
      >
        Ex
      </small>
    </span>
  );

  const InternalIcon = (
    <span className="fa-stack-lg" style={{ marginRight: '5px' }}>
      <i className="fa fa fa-desktop fa-stack-lg fa-lg"></i>
      <small
        style={{
          verticalAlign: 'top',
          marginLeft: '-15px',
          fontWeight: 'bold',
        }}
      >
        In
      </small>
      {/* <i className="fa fa-level-down-alt fa-xs" style={{verticalAlign: 'super', marginLeft: '-12px' }}></i> */}
    </span>
  );

  const AwsIcon = (
    <span className="fa-stack-lg" style={{ marginRight: '5px' }}>
      <i className="fa-brands fa-aws fa-lg"></i>
    </span>
  );

  function getToolTipLabels() {
    const toolTipLabels: TooltipLabelsType = [];
    if (epType === EPType.Internal || epType === EPType.All) {
      const internalCounts = hostCounts[EPType.Internal];
      toolTipLabels.push({
        label: EPType.Internal,
        hostsCount: internalCounts,
      });
    }

    if (epType === EPType.External || epType === EPType.All) {
      const externalCounts = hostCounts[EPType.External];
      toolTipLabels.push({
        label: EPType.External,
        hostsCount: externalCounts,
      });
    }

    if (epType === EPType.EC2 || epType === EPType.All) {
      const ec2Counts = hostCounts[EPType.EC2];
      toolTipLabels.push({
        label: 'AWS', // Label different then the enum type
        hostsCount: ec2Counts,
      });
    }
    return toolTipLabels;
  }

  return (
    <Fragment>
      <div className="container-fluid p-0">
        <nav className="navbar navbar-expand-sm bg-white border-top-0 border border-bottom-light sticky-top px-0">
          {/* <div className='row  w-100'> */}
          <div className="col">
            <div className="d-flex flex-row float-right">
              <SeverityFilterDropdown
                severityFiltersAtom={severityFiltersAtom}
              />
              {/* <IconButton id="externalButton" iconClass='fa fa-desktop fa-lg' tooltip={epType === EPType.External ? 'Show All' : 'External Only'} */}
              <IconButton
                id="externalButton"
                iconElement={ExternalIcon}
                tooltip={
                  epType === EPType.External ? 'Show All' : 'External Only'
                }
                active={epType === EPType.External}
                onClick={() => {
                  setEPType(
                    epType === EPType.External ? EPType.All : EPType.External,
                  );
                }}
              />
              <IconButton
                id="internalButton"
                iconElement={InternalIcon}
                tooltip={
                  epType === EPType.Internal ? 'Show All' : 'Internal Only'
                }
                active={epType === EPType.Internal}
                onClick={() => {
                  setEPType(
                    epType === EPType.Internal ? EPType.All : EPType.Internal,
                  );
                }}
              />
              <IconButton
                id="EC2Button"
                iconElement={AwsIcon}
                tooltip={epType === EPType.EC2 ? 'Show All' : 'AWS Only'}
                active={epType === EPType.EC2}
                onClick={() => {
                  setEPType(epType === EPType.EC2 ? EPType.All : EPType.EC2);
                }}
              />
            </div>
          </div>
          {/* </div> */}
        </nav>
        <Card>
          <CardHeader>
            <Row>
              <Col className="col-auto">
                <CardTitle tag="h4">
                  Vulnerabilities Trending History&nbsp;
                  <Button
                    outline={true}
                    color="primary"
                    size="sm"
                    onClick={() => setHistoryChartsOpen(!historyChartsOpen)}
                  >
                    <i
                      className={
                        historyChartsOpen
                          ? 'bi bi-chevron-up'
                          : 'bi bi-chevron-down'
                      }
                    ></i>
                  </Button>
                </CardTitle>
              </Col>
              <Col>
                <Collapse isOpen={historyChartsOpen}>
                  <div className="d-inline float-right">
                    <HistoryChartHeader
                      intervalAtom={intervalAtom}
                      showHistoryChartCountsAtom={showHistoryChartCountsAtom}
                      timeSpanAtom={timeSpanAtom}
                    />
                  </div>
                </Collapse>
              </Col>
            </Row>
          </CardHeader>
          <Collapse isOpen={historyChartsOpen}>
            <CardBody>
              <ErrorBoundary>
                <Suspense fallback={<LoadingScreen />}>
                  <HistoryCharts
                    tooltipLabels={getToolTipLabels()}
                    setModalComponentData={setModalComponentData}
                  />
                </Suspense>
              </ErrorBoundary>
            </CardBody>
          </Collapse>
        </Card>
        <Card>
          <CardHeader>
            <Row>
              <Col>
                <CardTitle tag="h4">
                  Current Vulnerabilities&nbsp;
                  <Button
                    outline={true}
                    color="primary"
                    size="sm"
                    onClick={() =>
                      setCurrentVulnChartsOpen(!currentVulnChartsOpen)
                    }
                  >
                    <i
                      className={
                        currentVulnChartsOpen
                          ? 'bi bi-chevron-up'
                          : 'bi bi-chevron-down'
                      }
                    ></i>
                  </Button>
                </CardTitle>
              </Col>
              <Col>
                <Collapse isOpen={currentVulnChartsOpen}>
                  <div className="d-inline float-right">
                    <IconButton
                      id="chartTypeButton"
                      iconClass={
                        currentChartType === ChartType.Bar
                          ? 'fa fa-chart-pie fa-lg'
                          : 'fa fa-chart-bar fa-lg'
                      }
                      tooltip={
                        currentChartType === ChartType.Bar
                          ? 'Display Pie Charts'
                          : 'Display Bar Charts'
                      }
                      onClick={() => {
                        setCurrentChartType(
                          currentChartType === ChartType.Bar
                            ? ChartType.Doughnut
                            : ChartType.Bar,
                        );
                      }}
                    />
                  </div>
                </Collapse>
              </Col>
            </Row>
          </CardHeader>
          <Collapse isOpen={currentVulnChartsOpen}>
            <CardBody>
              <div className="row">
                <Suspense fallback={<LoadingScreen />}>
                  <CurrentCharts
                    setModalComponentData={setModalComponentData}
                  />
                </Suspense>
              </div>
            </CardBody>
          </Collapse>
        </Card>
        <VulnerabilityMetrics />
      </div>
      <div ref={listingScrollRef} aria-hidden></div>
      <ErrorBoundary>
        <EPList setModalComponentData={setModalComponentData} />
      </ErrorBoundary>
      <ModalDialog
        title={modalComponentData?.title}
        subTitle={modalComponentData?.subTitle}
        childComponent={modalComponentData?.bodyComponent}
        onClose={() => {
          setModalComponentData(undefined);
          setIsObsoleteGroupingVulnListingOpen(false);
          setIsGroupingByQidVulnListingOpen(false);
        }}
      />
    </Fragment>
  );
};

export default EPDashboard;
